<template>
  <div>
    <ThemeDefaultMenuBreadcrumb v-if="breadcrumbItem" :items="breadcrumbItem" />
    <ThemeDefaultContainerPage
      :title="article.title"
      class="tw-pt-0 sm:tw-pt-11 lg:tw-pb-5 tw-pb-8"
      content-class="lg:tw-max-w-[900px] md:tw-max-w-[720px] tw-mx-auto"
      custom-margin="!tw-mb-5"
    >
      <template #category>
        <MediaImageLazyLoad
          :src="article?.imageCover?.large || ''"
          :placeholder-src="article?.imageCover?.tiny || ''"
          :alt="article.title"
          :ratio="1.9 / 1"
          custom-class="tw-rounded-none sm:tw-rounded-lg"
          class="tw-mb-5 -tw-mx-4 sm:tw-mx-0"
          seo-optimize
        />
        <NuxtLink
          :to="
            getPath({
              componentName: 'Article',
              categorySlug: article.articleCategory.slug,
            })
          "
          :title="article.articleCategory.title"
          class="tw-text-mb-title-5 lg:tw-text-dt-title-5 tw-text-secondary link-underline-hover"
        >
          {{ article.articleCategory.title }}
        </NuxtLink>
      </template>
      <template #detail>
        <div
          class="tw-text-mb-body-small sm:tw-text-dt-body-small tw-text-dark-gray tw-flex tw-justify-center tw-gap-4 tw-mt-2"
        >
          <div class="tw-flex tw-items-center tw-gap-1">
            <QIcon name="calendar_today" />
            {{ formattedDate }}
          </div>
          <div data-allow-mismatch class="tw-flex tw-items-center tw-gap-1">
            <QIcon name="visibility" /> {{ $t("global.viewer") }}
            {{ article.view?.toLocaleString() || '0' }}
          </div>
        </div>
      </template>
      <DisplayHTMLContent
        class="tw-mt-3"
        :model-value="article.content || ''"
      />
      <ThemeDefaultDisplayArticleAuthor
        v-if="author"
        :author="author"
        :author-id="article.authorId"
        class="tw-mt-5"
      />
      <div class="tw-flex tw-justify-between tw-mt-5 tw-mb-3">
        <Tags
          v-show="tags.length > 0"
          :items="tags"
          :category-event-name="categoryEventName"
          :event-name="eventName"
        />
        <div class="tw-hidden lg:tw-flex">
          <ButtonShare
            :category-event-name="categoryEventName"
            :title-name="article.title"
            :event-name="eventShareName"
            theme-primary-circle
          />
        </div>
      </div>
      <div class="lg:tw-hidden tw-flex tw-justify-end">
        <ButtonShare
          :category-event-name="categoryEventName"
          :title-name="article.title"
          :event-name="eventShareName"
          theme-primary-circle
        />
      </div>
      <div v-show="relatedArticlesWithTitle.length > 0">
        <HorizontalLine custom-class="lg:!tw-my-5 !tw-my-5" />
        <h2 class="tw-text-mb-title-2 lg:tw-text-dt-title-2 lg:tw-mb-5 tw-mb-5">
          {{ $t("global.relateOf", { title: slugPageInfo?.title || "" }) }}
        </h2>
        <div
          class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4"
        >
          <div
            v-for="item in relatedArticlesWithTitle.slice(0, 3)"
            :key="`relate-article-${item._id}`"
          >
            <ThemeDefaultCardArticle
              title-tag="h3"
              class="tw-hidden md:tw-block tw-h-full"
              :published-at="item.publishedAt"
              :image="item.imageCover"
              :title="item.title"
              :description="item.shortDescription"
              :category-title="item.articleCategory.title"
              :path="getPath({ componentName: 'Article', slug: item.slug })"
              custom-padding-content="lg:!tw-p-5"
              @click="eventArticleRelated(item.title)"
            />
            <CardHorizontal
              class="tw-flex md:tw-hidden"
              :image="item.imageCover"
              :description="item.shortDescription"
              :ratio="1.9 / 1"
              :date="item.publishedAt"
              :title="item.title"
              :path="getPath({ componentName: 'Article', slug: item.slug })"
              :category-title="item.articleCategory.title"
              :category-path="
                getPath({
                  componentName: 'Article',
                  categorySlug: item.articleCategory.slug,
                })
              "
              :col-first="5"
              custom-article-card-horizontal="tw-grid-cols-[144px_1fr] tw-gap-4"
              @click="eventArticleRelated(item.title)"
            />
          </div>
        </div>
      </div>
    </ThemeDefaultContainerPage>
  </div>
</template>

<script setup lang="ts">
import { buildFaqPageSchema, dedupeFaqItems, extractFaqsFromHtml } from '~/composables/utils/faq'

const {
  categoryEventName,
  eventName,
  eventShareName,
  article,
  relateArticles,
  breadcrumbItem,
  tags,
  author,
  getPath,
  slugPageInfo,
  eventArticleRelated,
} = await useDetailArticlePage();

const relatedArticlesWithTitle = computed(() =>
  relateArticles.value?.filter((item: Article) => item.title?.trim?.()) ?? [],
)

const faqItems = computed(() => dedupeFaqItems(extractFaqsFromHtml(article.value?.content || '')))
useHead(() => faqItems.value.length ? {
  script: [{ type: 'application/ld+json', children: JSON.stringify(buildFaqPageSchema(faqItems.value)) }],
} : {})

const { $shortDateFormat } = useNuxtApp();
const formattedDate = ref('-');
onMounted(() => {
  const date = article.value?.publishedAt || article.value?.createdAt;
  formattedDate.value = date ? ($shortDateFormat(date) as string) : '-';
});
</script>
